/**
 * airportLookup.js — Authoritative airport metadata lookup.
 *
 * Sprint-3 · SSOT/Booking-cycle audit (Feb-2026): replaces the 14-entry
 * `data/mock.js` `airports` array as the source of truth for country
 * classification. `airports.json` carries 5,379 IATA airports including
 * `countryCode` (ISO 3166-1 alpha-2) so domestic-vs-international detection
 * works for the entire global route map, not just the 14 hand-curated codes.
 *
 * Lookups are O(1) after the first call (built once into a Map).
 *
 * Pre-existing imports (`from '../data/mock'`) still work for callers that
 * use the airport list for UI auto-complete on the home page; this util
 * is the recommended replacement when you need country / nationality data.
 */
import airportsData from '../data/airports.json';

let _byCode = null;

const _index = () => {
  if (_byCode) return _byCode;
  _byCode = new Map();
  for (const a of airportsData) {
    if (a && a.code) _byCode.set(String(a.code).toUpperCase(), a);
  }
  return _byCode;
};

export const getAirportInfo = (code) => {
  if (!code) return null;
  return _index().get(String(code).toUpperCase()) || null;
};

export const getAirportCity = (code) => {
  return getAirportInfo(code)?.city || code || '';
};

export const getAirportName = (code) => {
  return getAirportInfo(code)?.name || code || '';
};

export const getAirportCountry = (code) => {
  return getAirportInfo(code)?.country || null;
};

export const getAirportCountryCode = (code) => {
  return getAirportInfo(code)?.countryCode || null;
};

/**
 * Returns true when the route between fromCode and toCode crosses an
 * international border. Falls back to `false` when either airport is
 * unknown — better to default to domestic than to demand passport
 * details from a user on a known-domestic IATA we just don't have in
 * the dataset (extremely rare with 5,379 entries, but defensive).
 */
export const isInternationalRoute = (fromCode, toCode) => {
  if (!fromCode || !toCode) return false;
  const fromCC = getAirportCountryCode(fromCode);
  const toCC = getAirportCountryCode(toCode);
  if (!fromCC || !toCC) return false;
  return fromCC !== toCC;
};
